import React from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';

export interface PieDataItem {
	name: string;
	value: number;
}

interface DissectionPieProps {
	title: string;
	data: PieDataItem[];
	height?: string | number;
}

const PALETTE = [
	'#1B5E80',
	'#E86E2D',
	'#1E6E27',
	'#13A8D7',
	'#A22A94',
	'#58AC2F',
	'#11364A',
	'#8F3A0E',
	'#103A1C',
];

const COLOR_MAP: Record<string, string> = {
	'Databricks': '#1B5E80',
	'Storage': '#E86E2D',
	'AKS': '#1E6E27',
	'DataFactory': '#13A8D7',
	'Network': '#A22A94',
	'Compute': '#13A8D7',
	'Synapse': '#58AC2F',
	'NetApp': '#11364A',
	'Sql': '#8F3A0E',
	'OperationalInsights': '#11364A',
	'Other Services': '#8F3A0E',
	'Other Services*': '#103A1C',
};

const DissectionPie: React.FC<DissectionPieProps> = ({ title, data, height = 400 }) => {
	const maxVal = Math.max(...data.map((d) => d.value));

	const formattedSeriesData = data.map((item, idx) => {
		const isLargest = item.value === maxVal;
		return {
			name: item.name,
			value: item.value,
			itemStyle: {
				color: COLOR_MAP[item.name] || PALETTE[idx % PALETTE.length],
				borderColor: '#ffffff',
				borderWidth: 2,
			},
			label: isLargest
				? {
						show: true,
						position: 'inside' as const,
						formatter: '{c}, {d}%',
						color: '#FFFFFF',
						fontSize: 12,
						fontWeight: 'bold' as const,
				  }
				: {
						show: true,
						position: 'outside' as const,
						formatter: '{c}, {d}%',
						color: '#444444',
						fontSize: 11,
				  },
			labelLine: isLargest
				? { show: false }
				: {
						show: true,
						length: 10,
						length2: 12,
						lineStyle: {
							color: '#888888',
							width: 1,
						},
				  },
		};
	});

	const option: EChartsOption = {
		title: {
			text: title,
			left: 'center',
			top: 10,
			textStyle: {
				fontSize: 18,
				fontWeight: 600,
				color: '#555555',
				fontFamily: 'Segoe UI, Roboto, sans-serif',
			},
		},
		tooltip: {
			trigger: 'item',
			backgroundColor: '#F4F7FA',
			borderColor: '#CCCCCC',
			borderWidth: 1,
			padding: [10, 14],
			extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 4px;',
			formatter: (params: any) => {
				const val = params.value;
				const percent = params.percent;
				return `
					<div style="font-family: Segoe UI, Roboto, sans-serif; color: #111111; text-align: left;">
						<div style="font-size: 13px; font-weight: 500; margin-bottom: 2px;">FY 2026 Cost</div>
						<div style="font-size: 15px; font-weight: 700; margin-bottom: 6px;">${val}</div>
						<div style="font-size: 12px; color: #555555; margin-bottom: 2px;">% of total</div>
						<div style="font-size: 14px; font-weight: 700;">(${percent}%)</div>
					</div>
				`;
			},
		},
		legend: {
			bottom: 10,
			left: 'center',
			icon: 'rect',
			itemWidth: 10,
			itemHeight: 10,
			textStyle: {
				color: '#555555',
				fontSize: 12,
			},
		},
		series: [
			{
				name: title,
				type: 'pie',
				radius: '52%',
				center: ['50%', '47%'],
				data: formattedSeriesData,
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
						shadowOffsetX: 0,
						shadowColor: 'rgba(0, 0, 0, 0.3)',
						borderColor: '#ffffff',
						borderWidth: 1,
					},
				},
			},
		],
	};

	return <ReactECharts option={option} style={{ width: '100%', height }} />;
};

export default DissectionPie;
